import { Button, Col, message, Row, Spin } from "antd";
import { FormInstance } from "antd/lib/form";
import { AxiosResponse } from "axios";
import moment from "moment";
import React, { Fragment, RefObject } from "react";
import entitlements from "../config/entitlements";
import {
  Category,
  FormFieldConfig,
  RMCompanyActivity,
  SelectOptionsInterface,
  Team,
  User,
} from "../helpers/shared-interfaces";
import {
  handleValidationErrors,
  request,
  responseHandler,
} from "../helpers/utilities";
import Card from "./Card";
import EntitledSection from "./EntitledSection";
import BaseForm from "./Form/BaseForm";
import ModalCreator from "./ModalCreator";
import { GET_CATEGORIES, GET_SERVICES } from "../gql/general";
import { GET_ALL_TEAMS } from "../gql/teams";
import { CREATE_COMPANY_ACTIVITY, GET_COMPANY_ACTIVITY_TYPES, UPDATE_COMPANY_ACTIVITY } from "../gql/companies";
import { generateDisplayErrorMessage } from "../helpers/utilities";


interface Props {
  style?: React.CSSProperties;
  companyActivity: RMCompanyActivity | undefined;
  companyId: string;
  isEditing: boolean;
  closeDrawer: (currentActivityId: string | undefined) => void;
  refreshList: () => void;
}

interface State {
  companyActivitiesFieldsToHide: string[];
  services: SelectOptionsInterface[];
  categories: Category[];
  subCategories: SelectOptionsInterface[];
  companyActivityTypes: SelectOptionsInterface[];
  teams: Team[];
  isLoading: boolean;
  users: User[];
  q: string;
  showConfirmCreateModal: boolean;
  fieldsToHide: string[];
}

const fieldsToBeValidated = [
  "status",
  "summary",
  "date",
  "companyActivityTypeId",
  "serviceId",
  "categoryId",
  "subCategoryId",
  "assigneeId",
  "teamId",
  "completedOn",
  "dateOfEstablishment"
];

interface EditingViewProps {
  reference: RefObject<FormInstance>;
  updateCompanyActivity: () => Promise<void>;
  companyActivity: RMCompanyActivity;
  formFields: FormFieldConfig[];
  fieldsToHide: string[];
  style?: React.CSSProperties;
}

export function EditingView(props: EditingViewProps): JSX.Element {
  return (
    <div
      style={{
        ...props.style,
      }}
    >
      <Row>
        <Col span={24}>
          <EntitledSection entitlement={entitlements.CompanyActivities.edit}>
            <div
              className="view-opportunity-row create-interaction-row"
              style={{
                marginTop: 0,
              }}
            >
              <BaseForm
                layout="vertical"
                reference={props.reference}
                fields={props.formFields}
                fieldsToHide={props.fieldsToHide}
              ></BaseForm>

              <Row>
                <Button
                  type="primary"
                  disabled={false}
                  onClick={props.updateCompanyActivity}
                  className="base-button"
                  style={{
                    position: "relative",
                    top: "10px",
                    right: "5px",
                    borderRadius: 0,
                    height: "33px",
                  }}
                >
                  Update
                </Button>
              </Row>
            </div>
          </EntitledSection>
        </Col>
      </Row>
    </div>
  );
}

export default class CompanyActivitySection extends React.Component<
  Props,
  State
> {
  reference: RefObject<FormInstance>;
  updateReference: RefObject<FormInstance>;
  constructor(props: Props) {
    super(props);
    this.state = {
      companyActivitiesFieldsToHide: [],
      services: [],
      categories: [],
      subCategories: [],
      companyActivityTypes: [],
      teams: [],
      isLoading: false,
      users: [],
      q: "",
      showConfirmCreateModal: false,
      fieldsToHide: ["completedOn"],
    };
    this.reference = React.createRef<FormInstance>();
    this.updateReference = React.createRef<FormInstance>();
  }

  setRef = (name: "reference", ref: React.RefObject<FormInstance>): void => {
    this[name] = ref;
  };

  getServices = (): void => {
    request({
      query: GET_SERVICES,
      variables: {
        departmentId: 6,
      },
    }).then(
      ({
        data,
      }: AxiosResponse<{
        data: {
          services: [];
        };
      }>): void => {
        this.setState({
          services: data.data.services,
        });
      },
    );
  };

  getCategories = (): void => {
    request({
      query: GET_CATEGORIES,
      variables: {
        departmentId: 6,
        forPurpose: "rm_activities",
      },
    }).then(
      ({
        data,
      }: AxiosResponse<{
        data: {
          categories: [];
        };
      }>): void => {
        this.setState({
          categories: data.data.categories,
        });
      },
    );
  };

  getSubCategory = (): void => {
    const { companyActivity } = this.props;
    this.setState({
      subCategories: companyActivity
        ? companyActivity?.subCategory
          ? [companyActivity.subCategory]
          : []
        : [],
    });
  };

  getcompanyActivityTypes = (): void => {
    request({
      query: GET_COMPANY_ACTIVITY_TYPES,
    }).then(
      ({
        data,
      }: AxiosResponse<{
        data: {
          companyActivityTypes: [];
        };
      }>): void => {
        this.setState({
          companyActivityTypes: data.data.companyActivityTypes,
        });
      },
    );
  };

  getAllTeams = (): void => {
    this.setState({ isLoading: true });
    request({
      query: GET_ALL_TEAMS,
    }).then(({ data: { data } }: any): void => {
      this.setState({
        teams: data.teams,
        isLoading: false,
      });
    });
  };

  loadSelectBoxValues = (): void => {
    this.getServices();
    this.getcompanyActivityTypes();
    this.getAllTeams();
    this.getCategories();
  };

  loadSelectBoxValuesForUpdate = (): void => {
    const { companyActivity } = this.props;
    if (companyActivity) {
      this.getSubCategory();
      this.handleStatusSelect(companyActivity?.status);
      this.handleTeamSelect(companyActivity?.team?.id);
    }
  };

  createCompanyActivity = async (): Promise<void> => {
    interface Response {
      companyActivity: RMCompanyActivity;
    }

    if (!this.reference.current) return;

    let data;

    try {
      data = await this.reference.current.validateFields(fieldsToBeValidated);
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    this.setState({ isLoading: true, showConfirmCreateModal: false });

    try {
      const response: AxiosResponse = await request({
        query: CREATE_COMPANY_ACTIVITY,
        variables: { ...data, companyId: this.props.companyId },
      });

      const { err } = responseHandler<Response>(
        response,
        "createCompanyActivity",
      );

      if (err) {
        message.error(`failed to create company activity - ${generateDisplayErrorMessage(err)}`);
      } else {
        message.success("Company activity created successfully");

        this.props.closeDrawer(undefined);
        this.props.refreshList();
        this.reference.current.resetFields();
      }
    } catch (error) {
      message.error(`failed to create client interaction - ${generateDisplayErrorMessage(error)}`);
    }
  };

  updateCompanyActivity = async (): Promise<void> => {
    interface Response {
      companyActivity: RMCompanyActivity;
    }

    if (!this.updateReference.current) return;

    let data;

    try {
      data = await this.updateReference.current.validateFields(
        fieldsToBeValidated,
      );
      if (data["status"] === "pending" && data["completedOn"])
        delete data.completedOn;
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    this.setState({ isLoading: true, showConfirmCreateModal: false });

    try {
      const response: AxiosResponse = await request({
        query: UPDATE_COMPANY_ACTIVITY,
        variables: {
          ...data,
          id: this.props?.companyActivity?.id,
          companyId: this.props.companyActivity?.company?.id,
        },
      });

      const { err } = responseHandler<Response>(
        response,
        "updateCompanyActivity",
      );

      if (err) {
        message.error(`failed to update company activity - ${generateDisplayErrorMessage(err)}`);
      } else {
        message.success("Company Activity updated successfully");

        this.props.closeDrawer(undefined);
        this.props.refreshList();
      }
    } catch (error) {
      message.error(`failed to create company activity - ${generateDisplayErrorMessage(error)}`);
    }
  };

  componentDidMount() {
    this.loadSelectBoxValues();
    this.loadSelectBoxValuesForUpdate();
  }

  getInteractionCreateForm = (
    activityFields: FormFieldConfig[],
    isLoading: boolean,
  ): JSX.Element => {
    return (
      <Spin spinning={isLoading}>
        <BaseForm
          layout="vertical"
          reference={this.reference}
          fields={activityFields}
          fieldsToHide={[]}
        >
          <></>
        </BaseForm>
      </Spin>
    );
  };

  handleCategorySelect = (id: string) => {
    const subCategories =
      this.state.categories.find((item) => item.id === id)?.subCategories ?? [];

    this.setState({
      subCategories: subCategories,
    });
  };

  handleStatusSelect = (status: string) => {
    let fieldsToHide = status === "completed" ? [] : ["completedOn"];

    this.setState({
      fieldsToHide,
    });
  };

  handleTeamSelect = (id: string) => {
    const { teams } = this.state;

    const teamSelected = teams.find((item) => item.id === id);

    this.setState({
      users: teamSelected?.users ?? [],
    });
  };

  toggleModal = (): void => {
    this.setState({
      showConfirmCreateModal: false,
    });
  };

  render(): JSX.Element {
    const {
      services,
      companyActivityTypes,
      categories,
      subCategories,
      teams,
      users,
      showConfirmCreateModal,
      fieldsToHide,
    } = this.state;

    let modifiedUsers: SelectOptionsInterface[] = users.map((user) => {
      return { ...user, id: user.id, name: user.name };
    });

    const modifiedTeams: SelectOptionsInterface[] = teams.map((team) => {
      return { id: team.id, name: team.name };
    });

    const status: SelectOptionsInterface[] = [
      { id: "completed", name: "completed" },
      { id: "pending", name: "pending" },
    ];

    const initalValue = this.props.companyActivity;
    if (this.props.isEditing && initalValue) {
      const companies: SelectOptionsInterface[] = [
        {
          id: initalValue?.company?.id ?? "",
          name: initalValue?.company?.name ?? "",
        },
      ];

      modifiedUsers = [
        ...modifiedUsers,
        { id: initalValue?.assignee?.id, name: initalValue?.assignee?.name },
      ];

      const updateActivityFields: FormFieldConfig[] = [
        {
          id: "companyId",
          name: "companyId",
          placeholder: "Company",
          required: true,
          disabled: true,
          inputType: "select",
          rule: "string",
          label: "Company",
          elementStyle: {
            width: "100%",
          },
          selectOptions: companies,
          initialValue: initalValue?.company?.id,
          colConfig: {
            span: 16,
          },
        },
        {
          id: "serviceId",
          name: "serviceId",
          placeholder: "Service",
          required: true,
          inputType: "select",
          rule: "string",
          label: "Service",
          elementStyle: {
            width: "100%",
          },
          selectOptions: services,
          initialValue: initalValue?.service?.id,
          colConfig: {
            span: 16,
          },
        },
        {
          id: "categoryId",
          name: "categoryId",
          placeholder: "Category",
          required: true,
          inputType: "select",
          rule: "string",
          label: "Category",
          elementStyle: {
            width: "100%",
          },
          selectOptions: categories,
          initialValue: initalValue?.category?.id,
          changeHandler: this.handleCategorySelect,
          selectHandler: this.handleCategorySelect,
          colConfig: {
            span: 16,
          },
        },
        {
          id: "subCategoryId",
          name: "subCategoryId",
          required: true,
          inputType: "select",
          rule: "string",
          label: "Sub Category",
          placeholder: "Sub Category",
          elementStyle: {
            width: "100%",
          },
          selectOptions: subCategories,
          initialValue: initalValue?.subCategory?.id,
          colConfig: {
            span: 16,
          },
        },
        {
          id: "companyActivityTypeId",
          name: "companyActivityTypeId",
          placeholder: "Client Activity Type",
          required: true,
          inputType: "select",
          rule: "string",
          label: "Client Activity Type",
          elementStyle: {
            width: "100%",
          },
          selectOptions: companyActivityTypes,
          initialValue: initalValue?.companyActivityType?.id,
          colConfig: {
            span: 16,
          },
        },
        {
          id: "date",
          name: "date",
          inputType: "defaultDate",
          rule: "object",
          required: true,
          label: "Date",
          initialValue: moment(initalValue?.date),
          elementStyle: {
            width: "100%",
          },
          colConfig: {
            span: 16,
          },
        },
        {
          id: "summary",
          name: "summary",
          required: true,
          inputType: "textArea",
          rule: "string",
          label: "Summary",
          placeholder: "Summary",
          initialValue: initalValue?.summary,
          colConfig: {
            span: 16,
          },
        },
        {
          id: "teamId",
          name: "teamId",
          rule: "string",
          inputType: "select",
          required: true,
          label: "Team",
          placeholder: "Team",
          selectOptions: modifiedTeams,
          initialValue: initalValue?.team?.id,
          changeHandler: this.handleTeamSelect,
          selectHandler: this.handleTeamSelect,
          elementStyle: {
            width: "100%",
          },
          colConfig: {
            span: 16,
          },
        },
        {
          id: "assigneeId",
          name: "assigneeId",
          placeholder: "Assignee",
          required: true,
          inputType: "search",
          rule: "string",
          label: "Assignee",
          isSearching: true,
          selectOptions: modifiedUsers,
          initialValue: initalValue?.assignee?.id,
          elementStyle: {
            width: "100%",
          },
          colConfig: {
            span: 16,
          },
        },
        {
          id: "status",
          name: "status",
          placeholder: "Status",
          required: true,
          inputType: "select",
          rule: "string",
          label: "Status",
          elementStyle: {
            width: "100%",
          },
          selectOptions: status,
          initialValue: initalValue.status,
          selectHandler: this.handleStatusSelect,
          changeHandler: this.handleStatusSelect,
          colConfig: {
            span: 16,
          },
        },
        {
          id: "completedOn",
          name: "completedOn",
          inputType: "defaultDate",
          rule: "object",
          required: true,
          label: "Completed On",
          initialValue: moment(initalValue?.date),
          elementStyle: {
            width: "100%",
          },
          colConfig: {
            span: 16,
          },
        },
      ];
      return (
        <EditingView
          reference={this.updateReference}
          updateCompanyActivity={this.updateCompanyActivity}
          companyActivity={initalValue}
          formFields={updateActivityFields}
          fieldsToHide={fieldsToHide}
        />
      );
    }

    const activityFields: FormFieldConfig[] = [
      {
        id: "serviceId",
        name: "serviceId",
        placeholder: "Service",
        required: true,
        inputType: "select",
        rule: "string",
        label: "Service",
        elementStyle: {
          width: "100%",
        },
        selectOptions: services,
        colConfig: {
          span: 16,
        },
      },
      {
        id: "categoryId",
        name: "categoryId",
        placeholder: "Category",
        required: true,
        inputType: "select",
        rule: "string",
        label: "Category",
        elementStyle: {
          width: "100%",
        },
        selectOptions: categories,
        changeHandler: this.handleCategorySelect,
        selectHandler: this.handleCategorySelect,
        colConfig: {
          span: 16,
        },
      },
      {
        id: "subCategoryId",
        name: "subCategoryId",
        required: true,
        inputType: "select",
        rule: "string",
        label: "Sub Category",
        placeholder: "Sub Category",
        elementStyle: {
          width: "100%",
        },
        selectOptions: subCategories,
        colConfig: {
          span: 16,
        },
      },
      {
        id: "companyActivityTypeId",
        name: "companyActivityTypeId",
        placeholder: "Company Activity Type",
        required: true,
        inputType: "select",
        rule: "string",
        label: "Company Activity Type",
        elementStyle: {
          width: "100%",
        },
        selectOptions: companyActivityTypes,
        colConfig: {
          span: 16,
        },
      },
      {
        id: "date",
        name: "date",
        inputType: "defaultDate",
        rule: "object",
        required: true,
        label: "Date",
        elementStyle: {
          width: "100%",
        },
        colConfig: {
          span: 16,
        },
      },
      {
        id: "summary",
        name: "summary",
        required: true,
        inputType: "textArea",
        rule: "string",
        label: "Summary",
        placeholder: "Summary",
        colConfig: {
          span: 16,
        },
      },
      {
        id: "teamId",
        name: "teamId",
        rule: "string",
        inputType: "select",
        required: true,
        label: "Team",
        placeholder: "Team",
        selectOptions: modifiedTeams,
        changeHandler: this.handleTeamSelect,
        selectHandler: this.handleTeamSelect,
        elementStyle: {
          width: "100%",
        },
        colConfig: {
          span: 16,
        },
      },
      {
        id: "assigneeId",
        name: "assigneeId",
        placeholder: "Assignee",
        required: true,
        inputType: "search",
        rule: "string",
        label: "Assignee",
        isSearching: true,
        selectOptions: modifiedUsers,
        elementStyle: {
          width: "100%",
        },
        colConfig: {
          span: 16,
        },
      },
      {
        id: "status",
        name: "status",
        placeholder: "Status",
        required: true,
        inputType: "select",
        rule: "string",
        label: "Status",
        elementStyle: {
          width: "100%",
        },
        selectOptions: status,
        selectHandler: this.handleStatusSelect,
        changeHandler: this.handleStatusSelect,
        colConfig: {
          span: 16,
        },
      },
      {
        id: "completedOn",
        name: "completedOn",
        inputType: "defaultDate",
        rule: "object",
        required: true,
        label: "Completed On",
        elementStyle: {
          width: "100%",
        },
        colConfig: {
          span: 16,
        },
      },
    ];

    return (
      <Fragment>
        {showConfirmCreateModal &&
          ModalCreator({
            title: "Create Company Activity",
            okType: "primary",
            okText: "Create",
            showModal: showConfirmCreateModal,
            onOkay: this.createCompanyActivity,
            toggleModal: this.toggleModal,
            inscription: "Are you sure you want to continue?",
          })}
        <div>
          <Spin spinning={false}>
            <Card header="New Company Activity">
              <Row>
                <Col span={16}>
                  <BaseForm
                    reference={this.reference}
                    fieldsToHide={fieldsToHide}
                    layout="vertical"
                    fields={activityFields}
                  />

                  <Row>
                    <Button
                      type="primary"
                      disabled={false}
                      onClick={() =>
                        this.setState({ showConfirmCreateModal: true })
                      }
                      className="base-button"
                      style={{
                        position: "relative",
                        top: "10px",
                        right: "5px",
                        borderRadius: 0,
                        height: "33px",
                      }}
                    >
                      Create
                    </Button>
                    <Button
                      type="primary"
                      disabled={false}
                      onClick={() => { }}
                      className="base-button"
                      style={{
                        position: "relative",
                        top: "10px",
                        right: "5px",
                        borderRadius: 0,
                        height: "33px",
                      }}
                    >
                      Cancel
                    </Button>
                    {ModalCreator({
                      title: "Cancel",
                      okType: "primary",
                      okText: "Yes",
                      width: 400,
                      showModal: false,
                      onOkay: () => { },
                      toggleModal: () => { },
                      showFooter: true,
                      inscription: `Are you sure you want to cancel request ?`,
                    })}
                  </Row>
                </Col>
              </Row>
            </Card>
          </Spin>
        </div>
      </Fragment>
    );
  }
}
